import type { PictureTreatment, SlideBackground, SlideLayoutTemplate } from '../../types/api';
import { DEFAULT_PICTURE_TREATMENT } from '../generation/pictureStyles';
import { SLIDE_TEMPLATES, type TemplateBox } from './slideTemplates';

/**
 * The picture treatments, as much of them as CSS can say (IMAGE_VARIETY_PLAN
 * §13 T3, T9).
 *
 * Mirrors of the backend render constants (app/rendering/render.py and
 * app/rendering/spec.py): canvas 1080, FRAME_INSET 24, FRAME_RADIUS 28,
 * FRAME_BORDER 10, FRAME_MARGIN 64, the full-canvas arch 760 x 880,
 * the cut-out box at 86% of the canvas, HALFTONE_CELL 14 and the grain fade
 * lifting blacks about 6%. Change one side, change the other.
 *
 * Every number is a fraction of the square slide, like `slideTemplates.ts`,
 * so the stage can be any size and still mean what the PNG means. The server
 * PNG stays the truth: the halftone here is a fixed dot screen over a toned
 * picture rather than dots sized by darkness, and the cut-out is the whole
 * picture contained rather than the subject lifted out of it.
 */

/** The renderer's canvas edge. Every geometry number below is a fraction of it. */
const CANVAS_SIZE = 1080;

function ofCanvas(pixels: number): number {
  return Math.round((pixels / CANVAS_SIZE) * 10000) / 10000;
}

/** How far a framed picture sits inside its slot (24/1080). */
export const FRAME_INSET = ofCanvas(24);

/** Corner radius of a framed picture (28/1080). */
export const FRAME_RADIUS = ofCanvas(28);

/** Accent border width round a framed picture (10/1080). */
export const FRAME_BORDER = ofCanvas(10);

/** How far a framed picture sits from every canvas edge on a full-canvas layout (64/1080). */
export const FRAME_MARGIN = ofCanvas(64);

/** The poster arch a full-canvas `masked` picture sits in (760 x 880, bottom-aligned). */
export const ARCH_WIDTH = ofCanvas(760);
export const ARCH_HEIGHT = ofCanvas(880);

/** The arch a `masked` picture sits in inside the top slot is 440 wide (MASK_TOP_ARCH_WIDTH). */
export const TOP_ARCH_WIDTH = ofCanvas(440);

/** The box a full-canvas cut-out subject is contained in, as a share of the canvas. */
export const CUTOUT_BOX = 0.86;

/**
 * Under a lower- or upper-third caption the full-canvas cut-out box keeps its
 * width but only 62% of its height, and moves to the half away from the type
 * (CUTOUT_ANCHORED_HEIGHT_FRACTION): 929 x 576 at y 75, or at y 429.
 */
export const CUTOUT_ANCHORED_HEIGHT = 0.62;

/** One halftone cell (14/1080). */
export const HALFTONE_CELL = ofCanvas(14);

/** How far the grain fade lifts the blacks (about 6%, as 15/255). */
export const GRAIN_LIFT = 15;

/** The whole canvas: the picture box of every template without a slot. */
const WHOLE_CANVAS_BOX: TemplateBox = { left: 0, top: 0, width: 1, height: 1 };

/** Only a picture is treated: a colour or gradient background ignores it (T2). */
export function isPictureBackground(background: SlideBackground): boolean {
  return background.kind === 'asset' || background.kind === 'generated';
}

/**
 * Does this treatment actually draw? Only on a picture background, and not on
 * the item, anniversary, profile-split and community renders, which compose
 * their own pictures and ignore it exactly as the renderer does.
 */
export function treatmentApplies(
  background: SlideBackground,
  layoutTemplate: SlideLayoutTemplate,
): boolean {
  if (!isPictureBackground(background)) return false;
  const kind = SLIDE_TEMPLATES[layoutTemplate].kind;
  return kind !== 'item' && kind !== 'community';
}

/** The treatment the stage should draw: the requested one, or full bleed where it is ignored. */
export function drawnTreatment(
  treatment: PictureTreatment,
  background: SlideBackground,
  layoutTemplate: SlideLayoutTemplate,
): PictureTreatment {
  return treatmentApplies(background, layoutTemplate) ? treatment : DEFAULT_PICTURE_TREATMENT;
}

/** The picture box: the template's image slot, else the whole canvas. */
export function pictureBox(layoutTemplate: SlideLayoutTemplate): TemplateBox {
  return SLIDE_TEMPLATES[layoutTemplate].imageBox ?? WHOLE_CANVAS_BOX;
}

/**
 * `tritone` and `halftone` are colour maps themselves, so they replace the
 * duotone map rather than stacking under it (T3).
 */
export function replacesDuotone(treatment: PictureTreatment): boolean {
  return treatment === 'tritone' || treatment === 'halftone';
}
